/*

#############################
    SSA Logger
    Henri DAVID
#############################

 SSALogger provides functionalities to logs SSA events from the browser.

 use :
 # let ssaLogger = new SSALogger('http://server/ssa', ssaEventDefinition);
 # ssaLogger.processAsync(); // will generate the first event with matching trigger and post it.
 # //AND/OR
 # ssaLogger.postEvent(<eventName>); // will generate and post an event of type eventName

 SSALogger depends on an eventDefinition :
 # let ssaEventDefinition = {};
 # ssaEventDefinition.Event = {};
 # ssaEventDefinition.Event.eventId = function(){return ssaLogger.helper.guid()};
 # ssaEventDefinition.Event.previous = function(){return ssaLogger.getLastEvent();}
 # ssaEventDefinition.Event.url = function(){return window.location.href};
 etc....

########################
# Getters

- "css" = get content of html element with CSS selector "ssa-<propertyName>"
      (variants "cssInt", "cssBool")
- "css:<cssSelector>" = get content of  element with CSS selector "<cssSelector>"
      (variants "cssInt:<cssSelector>", "cssBool:<cssSelector>")
- "cookie" = get cookie of the name "ssa-<propertiename>"
      (variants "cookieInt", "cookieBool")
- "cookie:<cookieName>" get cookie of name "cookieName"
      (variants "cookieInt:<cookieName>", "cookieBool:<cookieName>")
 - "urlParameter:<name>" get the url parameter of name "name"
    (variants "urlParameterInt:<name>", "urlParameterBoole:<name>")
- "const:<value>" get "value"
- "js" get variable named <propertyName>. "js:<name>"
- function =  you can assign a function, its return value will be used. (example = function(){ return myValue;})
- number =  the value of the assigned number will be used.
- boolean = the value of the assigned boolean will be used.
- regex :
      extract the first group of the regex and use it as value, or use regex.test() to validate the trigger
      you can test on url ("url:<regex>"), pathname ("pathname:<regex>"), parameters ("urlParameters:<regex>")

##############################
# Special Properties :

 _parent : define the name of the parent Class
 _trigger : define a regex. if the pathname part of the url matches, an event will be returned. the first matching event will be returned. process will stop there.
 _post: define a function(event) that will be called after processing the rest. useful to process custom things.
 _pre: define a function(event) that will be called BEFORE processing the rest. useful to process custom things.


// TODO / FIXME
- end of SearchUUID: on event that are not search related ?

############################
# History:
* 0.0.1 : initial version
* 0.0.2 : fix url encoding
* 0.0.3 : safer way to insert the pixel tracker.
* 0.0.4 : fix bug with new windows, add class and ids to pixelTrackers and minor changes for automated testing.
* 0.0.5 : introduction of recommenderUUID

*/
// import { logSSAEventToAPI } from "@/lib/utils";

let SSALogger = function (config) {
    this.eventDefinition = {} // must be provided !
    //defaults
    this.version = '0.0.5'
    this.debug = false
    this.defaultEvent = 'DefaultEvent'
    this.serviceUrl = null
    this.ssaPixelTrackerImageId = 0
    this.sessionTimeout = 30
    this.useStorage = true
    this.pixelTrackStyle = 'width:0;height:0;margin:0;padding:0;border:0;'

    // get config
    for (let key in config) {
        if (config.hasOwnProperty(key)) {
            this[key] = config[key]
        }
    }

    if (window.Cypress) {
        console.info('SSALogger: running within cypress for automated tests. setting debug on, using test ssa endpoint')
        this.debug = true
        this.serviceUrl = 'https://beta.eu-west-1.search.stepstone.com/ssa'
    }

    // init
    this.helper = new SSAHelper({
        useStorage: this.useStorage,
        debug: this.debug
    })

    // clear session if needed
    if (this.helper.storage) {
        const last = this.helper.storage.getItem('ssa-lastEvent-timestamp')
        const now = new Date().getTime()
        if (last && (now - last) / 1000 / 60 > this.sessionTimeout) {
            if (this.debug) {
                console.info(
                    'session expired. ' +
                    Math.floor((now - last) / 1000 / 60) +
                    'min old. (timeout: ' +
                    this.sessionTimeout +
                    'min.)'
                )
            }
            this.clear()
        }
    }

    // Process deferred events
    if (this.helper.storage) {
        setTimeout(
            function (me) {
                if(!me) return;
                me.processDeferredEvents()
            },
            0,
            this
        )
    }
}

SSALogger.prototype.logEventAsync = function (eventName) {
    setTimeout(this.logEventAsyncTrick, 0, eventName, this)
}

SSALogger.prototype.logEventAsyncTrick = function (eventName, me) {
    me.logEvent(eventName)
}

SSALogger.prototype.logEvent = function (eventName) {
    const event = this.createEvent(eventName)
    if (event) {
        this.postEvent(event)
        this.recordAsLastEvent(event)
    }
}

SSALogger.prototype.processAsync = function () {
    setTimeout(this.processAsyncTrick, 0, this)
}

SSALogger.prototype.processAsyncTrick = function (me) {
    me.process()
}

SSALogger.prototype.process = function () {
    const eventName = this.getEventNameFromTrigger()
    if (eventName) {
        this.logEvent(eventName)
    }
}

SSALogger.prototype.clear = function () {
    this.helper.clearStorage()
    window.name = ''
}

SSALogger.prototype.recordAsLastEvent = function (event) {
    if (event) {
        this.currentEvent = event
        // record searchUUID and eventId in window.name
        window.name =
            'ssa#' +
            (event.searchUUID ? event.searchUUID : '') + // 0
            '#' +
            (event.eventId ? event.eventId : '') // 1
        if (this.helper.storage) {
            // store event Id by referer in storage (used for new tabs)
            if (event.eventId) {
                this.helper.storage.setItem(
                    'ssa-lastEvent-' + window.location.href,
                    event.eventId
                )
            }
            if (event.searchUUID) {
                // store searchUUID by referer in storage (used for new tabs)
                this.helper.storage.setItem(
                    'ssa-searchUUID-' + window.location.href,
                    event.searchUUID
                )
                // store searchUUID per listingId for Listing events
                if (event.listingIds) {
                    for (const listingId in event.listingIds) {
                        this.helper.storage.setItem(
                            'ssa-searchUUID-' + listingId,
                            event.searchUUID
                        )
                    }
                }
            }
            if (event.recommenderUUID && event.listingIds) {
                // store recommenderUUID per listingId for Listing events
                for (const listingId in event.listingIds) {

                    this.helper.storage.setItem(
                        'ssa-recommenderUUID-' + event.listingIds[listingId],
                        event.recommenderUUID
                    )

                }
            }
        }
    }
}

SSALogger.prototype.getLastEvent = function () {
    let lastEvent
    if (this.currentEvent && this.currentEvent.eventId) {
        //if we have a current event, previous event is this one.
        lastEvent = this.currentEvent.eventId
        if (lastEvent && this.debug) {
            console.debug(
                'got previous event id from currentEvent ' +
                JSON.stringify(this.currentEvent)
            )
        }
    }
    //get last event id from window.name
    if (!lastEvent && window.name && window.name.lastIndexOf('ssa#') === 0) {
        lastEvent = window.name.substring(4)
        if (lastEvent && this.debug) {
            console.debug('got previous event id  from window.name ' + lastEvent)
        }
    }
    // get last event id by referrer
    if (!lastEvent && this.helper.storage) {
        lastEvent = this.helper.storage.getItem('ssa-lastEvent-' + document.referrer)
        if (lastEvent && this.debug) {
            console.debug('got previous event id  from by referer ' + lastEvent)
        }
    }
    return lastEvent
}

SSALogger.prototype.getAndSetSearchUUID = function (event) {
    //will be stored in storage for each referrer as we register the event as lastEvent.
    //this function should only be called for "Search" events.
    //it will be recorded while sending the event.
    return event && event.eventId ? event.eventId : this.helper.guid()
}

SSALogger.prototype.getSearchUUID = function () {
    let searchUUID
    // get searchUUID from window.name
    if (window.name && window.name.lastIndexOf('ssa#') === 0) {
        searchUUID = window.name.substring(4).split('#')[0]
        if (searchUUID && this.debug) {
            console.debug('got searchUUID from window.name ' + searchUUID)
        }
    }
    if (!searchUUID && this.helper.storage) {
        // get searchUUID from referer (for new tabs)
        searchUUID = this.helper.storage.getItem(
            'ssa-searchUUID-' + document.referrer
        )
        if (searchUUID && this.debug) {
            console.debug('got searchUUID from by referer ' + searchUUID)
        }
    }
    return searchUUID
}

SSALogger.prototype.getSearchUUIDForListingId = function (listingId) {
    // get the searchUUID for a listingId from local storage
    let searchUUID
    if (this.helper.storage) {
        searchUUID = this.helper.storage.getItem(
            'ssa-searchUUID-' + listingId
        )
        if (searchUUID && this.debug) {
            console.debug('got searchUUID from by referer ' + searchUUID)
        }
    }
    return searchUUID
}

SSALogger.prototype.getAndSetRecommenderUUID = function (event) {
    //will be stored in storage for each referrer as we register the event as lastEvent.
    //this function should only be called for "RecommenderListingsDisplayed" events.
    //it will be recorded while sending the event.
    return event && event.eventId ? event.eventId : this.helper.guid()
}


SSALogger.prototype.getRecommenderUUIDForListingId = function (listingId) {
    // get the recommenderUUID for a listingId from local storage
    let recommenderUUID


    if (this.helper.storage) {
        recommenderUUID = this.helper.storage.getItem(
            'ssa-recommenderUUID-' + listingId
        )
        if (recommenderUUID && this.debug) {
            console.debug('got recommenderUUID from by referer ' + recommenderUUID)
        }
    }
    return recommenderUUID
}

SSALogger.prototype.getEventNameFromTrigger = function () {
    for (let key in this.eventDefinition) {
        if (this.eventDefinition[key]._trigger) {
            let trigger = this.eventDefinition[key]._trigger
            let ignore = this.eventDefinition[key]._ignore
            if (trigger && !ignore) {
                let mustTrigger = false
                if (typeof trigger == 'function') {
                    if (trigger()) {
                        mustTrigger = true
                    }
                } else if (!mustTrigger && typeof trigger === 'string') {
                    if (trigger.lastIndexOf('url:', 0) === 0) {
                        let regex = new RegExp(trigger.substring(4))
                        mustTrigger = regex.test(window.location.href)
                    } else if (trigger.lastIndexOf('pathname:', 0) === 0) {
                        let regex = new RegExp(trigger.substring(9))
                        mustTrigger = regex.test(window.location.pathname)
                    } else {
                        console.error('unsupported trigger type: ' + trigger)
                    }
                } else {
                    console.error('unsupported trigger type: ' + trigger)
                }
                if (mustTrigger) {
                    return key
                }
            }
        }
    }
    //return default event if nothing else matches
    if (this.defaultEvent) {
        return this.defaultEvent
    }
}

SSALogger.prototype.createEvent = function (
    eventName,
    eventParameter,
    noPostFunction
) {
    if (this.eventDefinition[eventName]) {
        let ed = this.eventDefinition[eventName]
        //create empty map if needed
        let event = eventParameter ? eventParameter : {}
        //set objectName if needed
        if (!event.eventName) {
            event.eventName = eventName
        }
        //pre function
        if (ed._pre && typeof ed._pre === 'function') {
            ed._pre(event)
        }
        //recursively go to parent class
        if (ed._parent) {
            //call parent, disabling post function (, true);
            this.createEvent(ed._parent, event, true)
        }
        //populate data for myself
        for (const key in ed) {
            if (ed.hasOwnProperty(key)) {
                if (key.lastIndexOf('_', 0) !== 0 && ed[key]) {
                    let eventValue
                    if (typeof ed[key] === 'function') {
                        eventValue = ed[key](event) //call the function
                    } else if (typeof ed[key] === 'number') {
                        eventValue = ed[key]
                    } else if (typeof ed[key] === 'boolean') {
                        eventValue = ed[key]
                    } else if (typeof ed[key] === 'string') {
                        eventValue = this.createEventGetStringValue(ed[key], key, eventName)
                    } else {
                        console.error(
                            'SSALogger: unsupported type for ' +
                            eventName +
                            '.' +
                            key +
                            ': ' +
                            ed[key]
                        )
                    }
                    if (eventValue != null) {
                        event[key] = eventValue
                    }
                }
            }
        }
        //post function
        if (!noPostFunction) {
            this.createEventDoPostFunction(eventName, event)
        }
        return event
    } else {
        console.error('SSALogger: unknown event type: ' + eventName)
    }
}

SSALogger.prototype.createEventDoPostFunction = function (eventName, event) {
    if (this.eventDefinition[eventName]) {
        let ed = this.eventDefinition[eventName]
        if (ed._parent) {
            this.createEventDoPostFunction(ed._parent, event)
        }
        if (ed._post && typeof ed._post === 'function') {
            ed._post(event)
        }
    }
}

SSALogger.prototype.createEventGetStringValue = function (
    value,
    key,
    eventName
) {
    let eventValue = null

    if (value === 'css') {
        eventValue = this.helper.getCSS('#ssa-' + key)
    } else if (value === 'cssInt') {
        eventValue = parseInt(this.helper.getCSS('#ssa-' + key))
    } else if (value === 'cssBool') {
        eventValue = 'true' === this.helper.getCSS('#ssa-' + key)
    } else if (value.lastIndexOf('css:', 0) === 0) {
        eventValue = this.helper.getCSS(value.substring(4))
    } else if (value.lastIndexOf('cssInt:', 0) === 0) {
        eventValue = parseInt(this.helper.getCSS(value.substring(7)))
    } else if (value.lastIndexOf('cssBool:', 0) === 0) {
        eventValue = 'true' === this.helper.getCSS(value.substring(8))
    } else if (value === 'cookie') {
        eventValue = this.helper.getCookie('ssa-' + key)
    } else if (value === 'cookieInt') {
        eventValue = parseInt(this.helper.getCookie('ssa-' + key))
    } else if (value === 'cookieBool') {
        eventValue = 'true' === this.helper.getCookie('ssa-' + key)
    } else if (value.lastIndexOf('cookie:', 0) === 0) {
        eventValue = this.helper.getCookie(value.substring(7))
    } else if (value.lastIndexOf('cookieInt:', 0) === 0) {
        eventValue = parseInt(this.helper.getCookie(value.substring(10)))
    } else if (value.lastIndexOf('cookieBool:', 0) === 0) {
        eventValue = 'true' === this.helper.getCookie(value.substring(11))
    } else if (value === 'urlParameter') {
        eventValue = this.helper.getUrlParameter('ssa-' + key)
    } else if (value === 'urlParameterInt') {
        eventValue = parseInt(this.helper.getUrlParameter('ssa-' + key))
    } else if (value === 'urlParameterBool') {
        eventValue = 'true' === this.helper.getUrlParameter('ssa-' + key)
    } else if (value.lastIndexOf('urlParameter:', 0) === 0) {
        eventValue = this.helper.getUrlParameter(value.substring(13))
    } else if (value.lastIndexOf('urlParameterInt:', 0) === 0) {
        eventValue = parseInt(this.helper.getUrlParameter(value.substring(16)))
    } else if (value.lastIndexOf('urlParameterBool:', 0) === 0) {
        eventValue = 'true' === this.helper.getUrlParameter(value.substring(17))
    } else if (value === 'js') {
        try {
            eventValue = eval(key)
        } catch (e) {
            console.error("SSALogger: '" + key + "' does not exists as js variable")
        }
    } else if (value.lastIndexOf('js:', 0) === 0) {
        try {
            eventValue = eval(value.substring(3))
        } catch (e) {
            console.error(
                "SSALogger: '" + value.substring(3) + "' does not exists as js variable"
            )
        }
    } else if (value.lastIndexOf('const:', 0) === 0) {
        eventValue = value.substring(6)
    } else if (value.lastIndexOf('url:', 0) === 0) {
        const matches = window.location.href.match(value.substring(4))
        if (matches) {
            eventValue = matches[1]
        }
    } else if (value.lastIndexOf('pathname:', 0) === 0) {
        const matches = window.location.pathname.match(value.substring(9))
        if (matches) {
            eventValue = matches[1]
        }
    } else if (value.lastIndexOf('pathnameInt:', 0) === 0) {
        const matches = window.location.pathname.match(value.substring(12))
        if (matches) {
            eventValue = parseInt(matches[1])
        }
    } else {
        console.error(
            'SSALogger: unsupported type for ' + eventName + '.' + key + ': ' + value
        )
    }
    if (eventValue !== eventValue) {
        //handle NaN
        eventValue = null
    }
    if (eventValue === '') {
        //use a strict equal. because 0 == ""
        eventValue = null
    }
    if (eventValue != null) {
        return eventValue
    }
}

SSALogger.prototype.postEvent = function (event) {
    // store last event timestamp (used to expire session)
    if (this.helper.storage) {
        this.helper.storage.setItem('ssa-lastEvent-timestamp', new Date().getTime())
    }
    if (!event) {
        return
    }

    // search uuid for listing events
    if (!event.searchUUID && event.listingId) {
        event.searchUUID = this.getSearchUUIDForListingId(event.listingId)
    }

    // recommender uuid for search uuid for listing events
    if (!event.recommenderUUID && event.listingId) {
        event.recommenderUUID = this.getRecommenderUUIDForListingId(event.listingId)
    }

    // delete null values
    for (let i in event) {
        if (event.hasOwnProperty(i)) {
            if (event[i] === null) {
                delete event[i]
            }
        }
    }

    if (this.serviceUrl) {
        if (this.hasOwnProperty('brand') && this['brand'] === 'drushim') {

            // logSSAEventToAPI(event, event.eventName)
            //remove drushim properties
            delete event.ABTestCookie
            delete event.CookieExperation
        }

        let json = JSON.stringify(event, null, 2)

        let url = '?d=' + encodeURIComponent(json) + '&r=' + Math.random()
        //override URL for testing.
        if (typeof ssaTestURL === 'undefined') {
            url = this.serviceUrl + url
        } else {
            url = ssaTestURL + url
            console.info(
                'SSALogger: using injected url for automated test:' + ssaTestURL
            )
        }
        let element = document.createElement('img')
        element.src = url
        element.style = this.pixelTrackStyle
        element.className = 'ssaPixelTracker'
        element.id = 'ssaPixelTracker-' + this.ssaPixelTrackerImageId++ //increment pixel tracker counter for this page.
        if (document.body != null) {
            document.body.appendChild(element)
        }
    } else {
        console.info('SSALogger: no url set. skipping pixel tracker : ' + url)
    }
}

SSALogger.prototype.deferEvent = function (eventName) {
    if (this.helper.storage) {
        let ssaDeferredEvents = this.helper.storage.getItem('ssa-Deferred-Events')
        if (!ssaDeferredEvents) {
            ssaDeferredEvents = []
        } else {
            ssaDeferredEvents = JSON.parse(ssaDeferredEvents)
        }
        let event = this.createEvent(eventName)
        if (event) {
            ssaDeferredEvents.push(event)
            this.helper.storage.setItem(
                'ssa-Deferred-Events',
                JSON.stringify(ssaDeferredEvents)
            )
        }
    } else {
        if (this.debug) {
            console.warn(
                'SSALogger: Unable to defer event because useStorage is disabled'
            )
        }
    }
}

SSALogger.prototype.processDeferredEvents = function () {
    if (this.helper.storage) {
        let ssaDeferredEvents = this.helper.storage.getItem('ssa-Deferred-Events')
        if (ssaDeferredEvents) {
            ssaDeferredEvents = JSON.parse(ssaDeferredEvents)
            let event
            for (let x = 0; x < ssaDeferredEvents.length; x++) {
                event = ssaDeferredEvents[x]
                this.postEvent(event)
                this.recordAsLastEvent(event)
            }
            this.helper.storage.removeItem('ssa-Deferred-Events')
        }
    } else {
        if (this.debug) {
            console.warn(
                'SSALogger: deferred events not supported because useStorage is disabled'
            )
        }
    }
}

/***********************************************************************************************************************
 SSA Helper
 functions covering CSS selector, cookie and guid, url parameter etc.
 */

let SSAHelper = function (config) {
    this.debug = config.debug
    if (config.useStorage) {
        try {
            localStorage.setItem('abc', 'abc')
            localStorage.removeItem('abc')
            this.storage = localStorage
            this.sessionStorage = sessionStorage // as session one must understand, per tab.
            // see https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
        } catch (exception) { }
    }
}

SSAHelper.prototype.getUrlParameter = function (name) {
    try {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
        let regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
        let results = regex.exec(location.search)
        return results === null ?
            '' :
            decodeURIComponent(results[1].replace(/\+/g, ' '))
    } catch (error) {
        console.log('SSAHelper.prototype.getUrlParameter', error)
        return ''
    }
}

// get element by CSS Selectors
SSAHelper.prototype.getCSS = function (selectors) {
    let element = document.querySelector(selectors)
    if (element) {
        if (element.nodeName === 'INPUT') {
            return element.value
        } else {
            return element.innerHTML
        }
    } else {
        if (this.debug) {
            console.warn('SSALogger: missing HTML element: ' + selectors)
        }
    }
}

// Generate unique UUID
SSAHelper.prototype.guid = function () {
    let d = new Date().getTime()
    if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now() //use high-precision timer if available
    }
    //let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = (d + Math.random() * 16) % 16 | 0
        d = Math.floor(d / 16)
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

// cookies
// from https://gist.github.com/jonmagic/9521363
SSAHelper.prototype.getCookie = function (name) {
    try {
        let cookie =
            decodeURIComponent(
                document.cookie.replace(
                    new RegExp(
                        '(?:(?:^|.*;)\\s*' +
                        encodeURIComponent(name).replace(/[\-\.\+\*]/g, '\\$&') +
                        '\\s*\\=\\s*([^;]*).*$)|^.*$'
                    ),
                    '$1'
                )
            ) || null
        if (cookie) {
            return cookie
        } else {
            if (this.debug) {
                console.warn('SSALogger: missing cookie: ' + name)
            }
        }
    } catch (error) {
        console.log('SSAHelper.prototype.getCookie', error)
    }
}

SSAHelper.prototype.setCookie = function (
    sKey,
    sValue,
    vEnd,
    sPath,
    sDomain,
    bSecure
) {
    if (!sPath) {
        sPath = '/'
    }
    if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
        return false
    }
    let sExpires = ''
    if (vEnd) {
        switch (vEnd.constructor) {
            case Number:
                sExpires =
                    vEnd === Infinity ?
                        '; expires=Fri, 31 Dec 9999 23:59:59 GMT' :
                        '; max-age=' + vEnd
                break
            case String:
                sExpires = '; expires=' + vEnd
                break
            case Date:
                sExpires = '; expires=' + vEnd.toUTCString()
                break
        }
    }
    document.cookie =
        encodeURIComponent(sKey) +
        '=' +
        encodeURIComponent(sValue) +
        sExpires +
        (sDomain ? '; domain=' + sDomain : '') +
        (sPath ? '; path=' + sPath : '') +
        (bSecure ? '; secure' : '')
    return true
}

SSAHelper.prototype.removeCookie = function (sKey, sPath, sDomain) {
    if (!sKey || !this.hasItem(sKey)) {
        return false
    }
    document.cookie =
        encodeURIComponent(sKey) +
        '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
        (sDomain ? '; domain=' + sDomain : '') +
        (sPath ? '; path=' + sPath : '')
    return true
}

SSAHelper.prototype.cookieExists = function (sKey) {
    return new RegExp(
        '(?:^|;\\s*)' +
        encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') +
        '\\s*\\='
    ).test(document.cookie)
}

SSAHelper.prototype.cookieNames = function () {
    // this function is not in use
    let aKeys = document.cookie
        .replace(/((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g, '')
        .split(/\s*(?:\=[^;]*)?;\s*/)
    for (let nIdx = 0; nIdx < aKeys.length; nIdx++) {
        aKeys[nIdx] = decodeURIComponent(aKeys[nIdx])
    }
    return aKeys
}

// Storage
SSAHelper.prototype.clearStorage = function () {
    let arr = []
    if (this.storage) {
        for (let i = 0; i < this.storage.length; i++) {
            if (this.storage.key(i).substring(0, 4) === 'ssa-') {
                arr.push(this.storage.key(i))
            }
        }
        for (let i = 0; i < arr.length; i++) {
            this.storage.removeItem(arr[i])
        }
    }
    if (this.sessionStorage) {
        for (let i = 0; i < this.sessionStorage.length; i++) {
            if (this.sessionStorage.key(i).substring(0, 4) === 'ssa-') {
                if (this.sessionStorage.key(i).substring(0, 4) === 'ssa-') {
                    arr.push(this.sessionStorage.key(i))
                }
            }
            for (let i = 0; i < arr.length; i++) {
                this.sessionStorage.removeItem(arr[i])
            }
        }
    }
}

export { SSALogger, SSAHelper }

/*** EXPORTS FROM exports-loader ***/
exports["SSALogger"] = (SSALogger);
exports["SSAHelper"] = (SSAHelper);